import React from 'react';
import { css } from 'twin.macro';
import { HiOutlineCheckCircle, HiOutlineExclamationCircle } from 'react-icons/hi';
import OurAccordion from '@/_elements/OurAccordion';
import { FaWpforms } from 'react-icons/fa';

const stepData = [
    {
        number: 1,
        title: 'Fill Company Bank Account Information',
        description:
            'Add bank information for the company. This bank account will be used for company debits and paying out workers as necessary. Zeal will use this bank account for micro-deposit verification as part of the company onboarding process.',
    },
    {
        number: 2,
        title: 'Trigger Micro-Deposits',
        description:
            'Initiate micro-deposit amounts to the company bank account for verification. These deposits typically appear in 2-3 business days.',
    },
    {
        number: 3,
        title: 'Verify Micro-deposit Values',
        description: 'Submit the micro-deposit amounts to verify the company bank account.',
        note: {
            content: 'Micro-deposit values are always decimal values. Passing non-decimal values will cause fail.',
            color: '#fef3c7',
            borderColor: '#f59e0b',
        },
    },
    {
        number: 4,
        title: 'Sign Company Authorization Documents',
        description: 'Sign all required authorization documents to onboard the company. Users must sign:',
        list: ['Tax Information Terms', 'Reporting Agent Terms', 'Direct Deposit Forms', 'Terms and Conditions', '...'],
    },
    {
        number: 5,
        title: 'Complete Company Onboarding',
        description: 'Complete the company onboarding process once all requirements are met.',
        importantNote: {
            content:
                'Once micro-deposits have been confirmed and authorization documents signed, this endpoint completes onboarding for the specified company.',
            additionalNote: 'The company is initially set to a payment_permission_pending state when onboarded.',
            color: '#f3f4f6',
            borderColor: '#6b7280',
        },
    },
];

function Step({ number, title, description, note, list, importantNote }) {
    return (
        <div css={stepStyle}>
            <div css={stepNumberStyle}>{number}</div>
            <h4 css={stepTitleStyle}>{title}</h4>
            <p css={stepDescriptionStyle}>{description}</p>

            {note && (
                <div css={noteStyle(note.color, note.borderColor)}>
                    <strong>Note:</strong> {note.content}
                </div>
            )}

            {list && (
                <ul css={listStyle}>
                    {list.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </ul>
            )}

            {importantNote && (
                <div css={importantNoteStyle(importantNote.color, importantNote.borderColor)}>
                    <p css={importantNoteParagraphStyle}>
                        <strong>Important:</strong> {importantNote.content}
                    </p>
                    <p css={additionalNoteStyle}>
                        <strong>Note:</strong> The company is initially set to a
                        <code css={codeStyle}>payment_permission_pending</code>
                        state when onboarded. Refer to The Company Object for more information on company statuses.
                    </p>
                </div>
            )}
        </div>
    );
}

function ZealCompanyOnboardingHelpAccordion({ provider, companyID, show, teamId, handleUpdateProvider = () => {} }) {
    return (
        <OurAccordion
            key="provider-onboarding-accordion"
            title={
                <div css={titleContainerStyle}>
                    <FaWpforms />
                    Company Onboard Help
                    <HiOutlineExclamationCircle
                        color="#ff9800"
                        size="20px"
                        className="spin"
                        css={pulseAnimationStyle}
                    />
                </div>
            }
            defaultShow={false}
        >
            <div css={contentStyle}>
                <div css={headerStyle}>
                    <h3 css={headerTitleStyle}>Complete Company Onboarding Process</h3>
                    <p css={headerDescriptionStyle}>Follow these steps in order to successfully onboard a company.</p>
                </div>

                <div css={stepsContainerStyle}>
                    {stepData.map((step) => (
                        <Step key={step.number} {...step} />
                    ))}
                </div>

                <div css={completionNoteStyle}>
                    <div css={completionHeaderStyle}>
                        <HiOutlineCheckCircle className="text-success" size="20px" />
                        <strong css={completionTitleStyle}>Completion Checklist</strong>
                    </div>
                    <p css={completionDescriptionStyle}>
                        All 5 steps must be completed in order for the company to be fully onboarded and ready for
                        operations.
                    </p>
                </div>
            </div>
        </OurAccordion>
    );
}

// Styles
const titleContainerStyle = css`
    display: flex;
    gap: 12px;
    align-items: center;
`;

const pulseAnimationStyle = css`
    animation: pulse 4s infinite;

    @keyframes pulse {
        0% {
            transform: scale(1);
            opacity: 1;
        }
        50% {
            transform: scale(1.5);
            opacity: 0.8;
        }
        100% {
            transform: scale(1);
            opacity: 1;
        }
    }
`;

const contentStyle = css`
    overflow: auto;
    padding: 16px;
`;

const headerStyle = css`
    margin-bottom: 24px;
`;

const headerTitleStyle = css`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #333;
`;

const headerDescriptionStyle = css`
    font-size: 14px;
    color: #666;
    margin-bottom: 20px;
`;

const stepsContainerStyle = css`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const stepStyle = css`
    padding-left: 24px;
    position: relative;
`;

const stepNumberStyle = css`
    position: absolute;
    left: 0;
    top: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #50aeb0;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
`;

const stepTitleStyle = css`
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 6px;
    color: #111827;
`;

const stepDescriptionStyle = css`
    font-size: 14px;
    color: #4b5563;
    line-height: 1.5;
`;

const noteStyle = (backgroundColor, borderColor) => css`
    margin-top: 8px;
    padding: 8px 12px;
    background-color: ${backgroundColor};
    border-radius: 6px;
    border-left: 3px solid ${borderColor};
`;

const listStyle = css`
    margin-top: 8px;
    margin-left: 16px;
`;

const importantNoteStyle = (backgroundColor, borderColor) => css`
    margin-top: 12px;
    padding: 12px;
    background-color: ${backgroundColor};
    border-radius: 6px;
    border-left: 3px solid ${borderColor};
`;

const importantNoteParagraphStyle = css`
    margin-bottom: 8px;
`;

const additionalNoteStyle = css`
    margin: 0;
    font-size: 13px;
`;

const codeStyle = css`
    background-color: #e5e7eb;
    padding: 2px 6px;
    border-radius: 3px;
    margin: 0 4px;
`;

const completionNoteStyle = css`
    margin-top: 24px;
    padding: 16px;
    background-color: #d1fae5;
    border-radius: 8px;
    border-left: 4px solid #10b981;
`;

const completionHeaderStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const completionTitleStyle = css`
    font-size: 14px;
`;

const completionDescriptionStyle = css`
    font-size: 14px;
    color: #065f46;
    margin: 0;
`;
export const ZealStep = ({ number, title, description }) => (
    <div css={stepStyle}>
        <div css={stepNumberStyle}>{number}</div>
        <h3>
            <strong>{title}</strong>
        </h3>
        <p css={stepDescriptionStyle}>{description}</p>
    </div>
);

export default ZealCompanyOnboardingHelpAccordion;
